import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Key, ReactNode } from 'react';
import type { SortDescriptor, LoadingState } from '@react-types/shared';
import { DataTable, ColumnDefinition } from '../common/DataTable';
import { appendUniqueBy } from '../../utils/appendUnique';
import type {
  DomainSortKey,
  OverviewDomainRow,
  SortDirection,
} from '../../types/overview';
import {
  contentQualityBucket,
  formatCount,
  formatRelativeTime,
  formatScore,
  scoreBucket,
} from './formatters';

/** Sort keys that the backend accepts (kept in lock-step with the API). */
const SORTABLE_COLUMNS: ReadonlySet<DomainSortKey> = new Set<DomainSortKey>([
  'site_name',
  'content_quality',
  'ingested_count',
  'experiments',
  'deployed_assistants',
  'avg_user_rating',
  'last_used_at',
  'unanswered_count',
  'avg_llm_rating',
  'liked_count',
  'disliked_count',
  'total_queries',
]);

/** Appends a page of rows, skipping any `site_id` already present. */
export function appendUniqueDomainRows(
  prev: OverviewDomainRow[],
  next: OverviewDomainRow[],
): OverviewDomainRow[] {
  return appendUniqueBy(prev, next, (r) => r.site_id);
}

interface DomainsOverviewTableProps {
  rows: OverviewDomainRow[];
  tableLabel?: string;
  toolbarStart?: ReactNode;
  toolbarEnd?: ReactNode;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  /** Vertical sizing, forwarded to DataTable. Defaults to `'fill'` since this
   *  table is always hosted in a bounded, full-height overview/detail panel. */
  layout?: 'fill' | 'fixed';
  sortBy: DomainSortKey;
  sortDir: SortDirection;
  onSortChange: (sortBy: DomainSortKey, sortDir: SortDirection) => void;
  onRowClick?: (siteId: string) => void;
  loading?: boolean;
  loadingState?: LoadingState;
  onLoadMore?: () => void;
}

const ALL_COLUMNS = (t: TFunction): ColumnDefinition<OverviewDomainRow>[] => [
  {
    key: 'site_name',
    label: t('overview.domainsTable.columns.name'),
    allowsResizing: true,
    isRowHeader: true,
    allowsSorting: true,
    minWidth: 180,
    pinned: true,
    render: (r) => (
      <span>{r.site_name}</span>
    ),
    align: 'start',
  },
  {
    key: 'ingested_count',
    label: t('overview.domainsTable.columns.ingestedCount'),
    allowsResizing: true,
    allowsSorting: true,
    render: (r) => formatCount(r.ingested_count),
  },
  {
    key: 'total_queries',
    label: t('overview.domainsTable.columns.totalQueries'),
    allowsSorting: true,
    allowsResizing: true,
    render: (r) => formatCount(r.total_queries),
  },
  {
    key: 'unanswered_count',
    label: t('overview.domainsTable.columns.unanswered'),
    allowsSorting: true,
    allowsResizing: true,
    render: (r) => formatCount(r.unanswered_count),
  },
  {
    key: 'avg_llm_rating',
    label: t('overview.domainsTable.columns.avgLlmRating'),
    defaultVisible: false,
    allowsSorting: true,
    allowsResizing: true,
    render: (r) =>
      r.avg_llm_rating == null ? (
        <span className="overview-cell-dash">--</span>
      ) : (
        <span>
          <span
            className={`overview-pill overview-pill--${scoreBucket(r.avg_llm_rating)}`}
          >
            {formatScore(r.avg_llm_rating)}
          </span>
          {' '}
          <span className="overview-cell-sub">{t('overview.domainsTable.evals', { replace: { count: formatCount(r.llm_eval_count) } })}</span>
        </span>
      ),
  },
  {
    key: 'liked_count',
    label: t('overview.domainsTable.columns.userRating'),
    allowsSorting: true,
    allowsResizing: true,
    render: (r) => {
      const parts: string[] = [];
      if (r.liked_count > 0) parts.push(t('overview.domainsTable.likes', { replace: { count: formatCount(r.liked_count) } }));
      if (r.disliked_count > 0) parts.push(t('overview.domainsTable.dislikes', { replace: { count: formatCount(r.disliked_count) } }));
      return parts.length > 0 ? (
        <span>{parts.join(' / ')}</span>
      ) : (
        <span className="overview-cell-dash">--</span>
      );
    },
  },
  {
    key: 'content_quality',
    label: t('overview.domainsTable.columns.contentQuality'),
    defaultVisible: false,
    allowsResizing: true,
    allowsSorting: true,
    render: (r) =>
      r.content_quality == null ? (
        <span className="overview-cell-dash">--</span>
      ) : (
        <span
          className={`overview-pill overview-pill--${contentQualityBucket(r.content_quality)}`}
        >
          {formatScore(r.content_quality)}
        </span>
      ),
  },
  {
    key: 'experiments',
    label: t('overview.domainsTable.columns.experiments'),
    defaultVisible: false,
    allowsResizing: true,
    allowsSorting: true,
    render: (r) => formatCount(r.experiments),
  },
  {
    key: 'deployed_assistants',
    label: t('overview.domainsTable.columns.deployedAssistants'),
    defaultVisible: false,
    allowsSorting: true,
    allowsResizing: true,
    render: (r) => formatCount(r.deployed_assistants),
  },
  {
    key: 'last_used_at',
    label: t('overview.domainsTable.columns.lastUsed'),
    allowsSorting: true,
    allowsResizing: true,
    render: (r) => formatRelativeTime(r.last_used_at),
  },
];

export const DomainsOverviewTable: React.FC<DomainsOverviewTableProps> = ({
  rows,
  tableLabel,
  toolbarStart,
  toolbarEnd,
  emptyStateTitle,
  emptyStateDescription,
  layout = 'fill',
  sortBy,
  sortDir,
  onSortChange,
  onRowClick,
  loading = false,
  loadingState,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const columns = useMemo(() => ALL_COLUMNS(t), [t]);
  const resolvedEmptyTitle = emptyStateTitle ?? t('overview.domainsTable.emptyTitle');
  const resolvedEmptyDescription =
    emptyStateDescription ?? t('overview.domainsTable.emptyDescription');

  const sortDescriptor: SortDescriptor = useMemo(
    () => ({
      column: sortBy,
      direction: sortDir === 'asc' ? 'ascending' : 'descending',
    }),
    [sortBy, sortDir],
  );

  const handleSortChange = useCallback(
    (desc: SortDescriptor) => {
      const col = desc.column as DomainSortKey | undefined;
      if (!col || !SORTABLE_COLUMNS.has(col)) return;
      onSortChange(col, desc.direction === 'ascending' ? 'asc' : 'desc');
    },
    [onSortChange],
  );

  const handleRowAction = useCallback(
    (key: Key) => {
      if (onRowClick && typeof key === 'string') onRowClick(key);
    },
    [onRowClick],
  );

  return (
    <DataTable<OverviewDomainRow>
      ariaLabel={t('overview.domainsTable.ariaLabel')}
      items={rows}
      columns={columns}
      getRowKey={(r) => r.site_id}
      sortDescriptor={sortDescriptor}
      onSortChange={handleSortChange}
      onRowAction={onRowClick ? handleRowAction : undefined}
      loading={loading}
      loadingState={loadingState}
      onLoadMore={onLoadMore}
      density="regular"
      layout={layout}
      minHeight="size-1200"
      emptyStateTitle={resolvedEmptyTitle}
      emptyStateDescription={resolvedEmptyDescription}
      configurableColumns
      firstColumnDivider
      tableLabel={tableLabel}
      toolbarStart={toolbarStart}
      toolbarEnd={toolbarEnd}
    />
  );
};
